import { FC } from 'react';
import {
    AppIcon,
    Select,
    Theme,
    AppText,
    ReactSelectComponents,
    Box,
    Row,
    ExtractProps,
} from '@streem/ui-react';
import { getPreferredVideoInputId, setPreferredVideoInputId } from '@streem/toolbox';
import { recordElementClicked } from '@streem/analytics';
import { DeviceSettingsWrapper } from './shared_components';
import { useDeviceList } from '../../hooks/use_device_list';
import { useVideoDevices } from '../../hooks/use_devices';

type CameraOptionValue = MediaDeviceInfo & {
    displayLabel: string;
};
type SelectStyles = ExtractProps<typeof Select>['styles'];

interface VideoDropdownSelectorProps {
    onOpenDeviceSettings: () => void;
}
const videoDropdownOpenedEvent = 'nav-video-output-select-opened';
const videoOptionSelectedEvent = 'nav-video-output-item-selected';
const mediaSettingsOpenedId = 'nav-media-settings-opened';

export const VideoDropdownSelector: FC<React.PropsWithChildren<VideoDropdownSelectorProps>> = ({
    onOpenDeviceSettings,
}) => {
    const videoDevices = useVideoDevices();
    const { deviceList } = useDeviceList(
        videoDevices.videoDevices,
        getPreferredVideoInputId,
        setPreferredVideoInputId,
    );

    const handleSelectedOption = (option: CameraOptionValue) => {
        setPreferredVideoInputId(option.deviceId);
        recordElementClicked(videoOptionSelectedEvent);
    };
    const handleOpenDeviceSettings = () => {
        onOpenDeviceSettings();
        recordElementClicked(mediaSettingsOpenedId);
    };

    const onMenuOpen = () => {
        recordElementClicked(videoDropdownOpenedEvent);
    };

    const hasVideoPermission = videoDevices.hasVideoPermission;

    return (
        <Box mr="1.5rem">
            <Select
                id="nav-video-dropdown-select"
                isSearchable={false}
                onMenuOpen={onMenuOpen}
                onSelect={option => {
                    handleSelectedOption(option as unknown as CameraOptionValue);
                }}
                formatOptionLabel={RenderLabel}
                components={{
                    NoOptionsMessage: props => {
                        return (
                            <ReactSelectComponents.NoOptionsMessage {...props}>
                                <AppText color="grey40" size="medium">
                                    No Options Available
                                </AppText>
                            </ReactSelectComponents.NoOptionsMessage>
                        );
                    },
                    ClearIndicator: () => null,
                    IndicatorSeparator: () => null,
                    Placeholder: props => {
                        const iconName = hasVideoPermission ? 'CameraIcon' : 'CameraOffIcon';
                        const iconColor = hasVideoPermission
                            ? Theme.colors.black
                            : Theme.colors.red50;
                        return (
                            <ReactSelectComponents.Placeholder {...props}>
                                <AppIcon name={iconName} color={iconColor} slashcolor={iconColor} />
                            </ReactSelectComponents.Placeholder>
                        );
                    },
                    SingleValue,
                    MenuList: props => {
                        return (
                            <ReactSelectComponents.MenuList {...props}>
                                <Box p={'0.5rem 5rem 0.5rem 1rem'}>
                                    <AppText size="medium" bold fontWeight={700}>
                                        Select a Camera
                                    </AppText>
                                </Box>

                                <div>{props.children}</div>

                                <DeviceSettingsWrapper
                                    p={'0.75rem 5.4rem 0.75rem 1rem'}
                                    borderTop={`0.065rem ${Theme.colors.grey10} solid`}
                                    onClick={handleOpenDeviceSettings}
                                >
                                    <AppText size="medium" bold fontWeight={700}>
                                        Device Settings
                                    </AppText>
                                </DeviceSettingsWrapper>
                            </ReactSelectComponents.MenuList>
                        );
                    },
                }}
                options={deviceList}
                styles={
                    {
                        singleValue: base => ({
                            ...base,
                            marginLeft: '0px',
                            overflow: 'unset',
                        }),
                        noOptionsMessage: base => ({
                            ...base,
                            textAlign: 'center',
                        }),
                        placeholder: base => ({
                            ...base,
                            marginLeft: '0px',
                        }),
                        control: (base, { selectProps }) => ({
                            ...base,
                            width: '68px',
                            height: '2.5rem',
                            borderRadius: '2rem',
                            border: 'none',
                            cursor: 'pointer',
                            background: selectProps.isDisabled ? Theme.colors.grey10 : undefined,
                            ':hover': {
                                background: Theme.colors.blue05,
                            },
                        }),
                        menu: base => ({
                            ...base,
                            marginTop: '1.1rem',
                            borderRadius: 4,
                            right: 0,
                            width: '13.25rem',
                        }),
                        menuList: base => ({
                            ...base,
                            padding: 0,
                        }),
                        option: base => ({
                            ...base,
                            borderRadius: '0.25rem',
                            overflow: 'hidden',
                            backgroundColor: 'none',
                            ':hover': { backgroundColor: Theme.colors.blue05, cursor: 'pointer' },
                        }),
                        dropdownIndicator: (base, { selectProps }) => ({
                            ...base,
                            transform: selectProps.menuIsOpen ? 'rotate(180deg)' : null,
                            transition: 'all .2s ease',
                            color: Theme.colors.grey50,
                            ':hover': {
                                color: Theme.colors.grey50,
                            },
                        }),
                    } as SelectStyles
                }
            />
        </Box>
    );
};

type RenderLabel = ExtractProps<typeof Select>['formatOptionLabel'];
const RenderLabel: RenderLabel = data => {
    const option = data as CameraOptionValue;
    return (
        <OptionLabel
            label={option.displayLabel}
            value={option}
            selectedOption={getPreferredVideoInputId()}
        />
    );
};

const SingleValue: typeof ReactSelectComponents.SingleValue = props => {
    return (
        <ReactSelectComponents.SingleValue {...props}>
            <AppIcon name="CameraIcon" color={Theme.colors.black} />
        </ReactSelectComponents.SingleValue>
    );
};

const OptionLabel = ({
    label,
    selectedOption,
    value,
}: {
    label: string;
    selectedOption: string;
    value: CameraOptionValue;
}) => {
    const isSelectedOption = value?.deviceId === selectedOption;
    return (
        <Row alignItems="center" id="row" style={{ gap: '.56rem' }}>
            {isSelectedOption && <AppIcon name="DoneIcon" color={Theme.colors.black} />}
            {/* Hack to keep left margin between text */}
            {!isSelectedOption && <span style={{ width: '1.5rem', height: '1.5rem' }} />}
            <AppText as="span">{label}</AppText>
        </Row>
    );
};
